import { IntakeData } from '../context/IntakeContext'
import { buildIntakeReport } from './buildIntakeReport'
import { APP_BASE_URL } from './submitIntake'
import { ReportEntry, ReportQuestion } from '../types/intakeReport'

/**
 * Printbare HTML-versie (voor PDF) van de intake.
 * Gebruikt hetzelfde rapport als de Markdown-export, zodat beide bestanden
 * altijd exact dezelfde vragen en antwoorden bevatten.
 */
export function generateIntakeHTML(
  data: IntakeData,
  uploadedFiles: Record<string, File> = {},
): string {
  const report = buildIntakeReport(data, uploadedFiles)

  const sectionsHtml = report.sections
    .map(
      (section, index) => `
      <h2>${index + 1}. ${escapeHtml(section.title)}</h2>
      <p class="purpose">${escapeHtml(section.purpose)}</p>
      ${renderEntries(section.entries)}
    `,
    )
    .join('')

  const filesHtml = report.files.length
    ? `<ul class="file-list">${report.files
        .map(
          (f) =>
            `<li><strong>${escapeHtml(f.label)}</strong><br>Bijlage in mail: ${escapeHtml(f.fileName)}</li>`,
        )
        .join('')}</ul>`
    : '<p class="empty">Er zijn geen bestanden geüpload.</p>'

  return `<!DOCTYPE html>
<html lang="nl">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Noord Digital Intake – ${escapeHtml(report.clientName)}</title>
  <style>${styles}</style>
</head>
<body>
  <button class="print-btn" onclick="window.print()">Afdrukken / Opslaan als PDF</button>
  <h1>Website intake — ${escapeHtml(report.clientName)}</h1>
  <p class="meta">Datum: ${escapeHtml(report.generatedAt)}</p>
  ${sectionsHtml}
  <h2>Aangeleverde bestanden</h2>
  <p class="purpose">Alle bestanden zitten als bijlage in de intake-mail.</p>
  ${filesHtml}
  <div class="footer">
    Automatisch gegenereerd via de <a href="${APP_BASE_URL}">Noord Digital Intake</a>.
  </div>
</body>
</html>`
}

function renderEntries(entries: ReportEntry[]): string {
  let html = ''
  let rows = ''
  const flushRows = () => {
    if (rows) html += `<table>${rows}</table>`
    rows = ''
  }
  entries.forEach((entry) => {
    if (entry.type === 'group') {
      flushRows()
      html += `<div class="item-box"><h3>${escapeHtml(entry.title)}</h3><table>${entry.items
        .map(renderRow)
        .join('')}</table></div>`
      return
    }
    if (entry.type === 'colors') {
      const value = entry.colors.length
        ? entry.colors
            .map(
              (c) =>
                `<span class="swatch" style="background:${escapeAttr(c.hex)}"></span>${escapeHtml(c.name || 'Kleur')} (${escapeHtml(c.hex)})`,
            )
            .join('<br>')
        : '<span class="empty">Niet ingevuld</span>'
      rows += `<tr><th>${escapeHtml(entry.question)}</th><td>${value}</td></tr>`
      return
    }
    rows += renderRow(entry)
  })
  flushRows()
  return html
}

function renderRow(item: ReportQuestion): string {
  let value: string
  if (!item.answer) {
    value = '<span class="empty">Niet ingevuld</span>'
  } else {
    value = escapeHtml(item.answer).replace(/\n/g, '<br>')
  }
  return `<tr><th>${escapeHtml(item.question)}</th><td>${value}</td></tr>`
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function escapeAttr(value: string): string {
  return escapeHtml(value).replace(/'/g, '&#39;')
}

const styles = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; line-height: 1.55; color: #333; max-width: 820px; margin: 0 auto; padding: 24px; }
  .print-btn { display: block; margin: 0 auto 24px; padding: 12px 28px; background: #007AFF; color: #fff; border: none; border-radius: 8px; font-size: 16px; cursor: pointer; }
  h1 { color: #007AFF; border-bottom: 2px solid #007AFF; padding-bottom: 10px; margin-bottom: 8px; }
  .meta { color: #666; margin-bottom: 8px; }
  h2 { color: #1a1a1a; margin-top: 32px; background: #f8f9fa; padding: 10px 12px; border-radius: 5px; border-left: 4px solid #007AFF; font-size: 18px; }
  h3 { color: #333; font-size: 15px; margin-bottom: 6px; }
  .purpose { color: #777; font-size: 13px; font-style: italic; margin: 6px 0 4px; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th, td { padding: 9px 10px; border-bottom: 1px solid #eee; text-align: left; vertical-align: top; font-size: 14px; }
  th { width: 40%; color: #666; font-weight: normal; }
  td { font-weight: bold; }
  .empty { color: #b0b0b0; font-weight: normal; font-style: italic; }
  .item-box { border: 1px solid #eee; padding: 14px; margin-top: 12px; border-radius: 6px; background: #fafafa; page-break-inside: avoid; }
  .swatch { display: inline-block; width: 14px; height: 14px; border: 1px solid #ccc; border-radius: 3px; vertical-align: middle; margin-right: 6px; }
  a { color: #007AFF; }
  .file-list { list-style: none; margin-top: 8px; }
  .file-list li { margin-bottom: 10px; padding: 10px; background: #f0f7ff; border-radius: 6px; border: 1px solid #cce4ff; word-break: break-all; font-size: 14px; }
  .footer { margin-top: 48px; padding-top: 16px; border-top: 1px solid #eee; font-size: 12px; color: #999; text-align: center; }
  @media print {
    .print-btn { display: none !important; }
    a { color: #333; }
    h2 { page-break-after: avoid; }
  }
`
